'use client';

import { c as _c } from "react/compiler-runtime";
import type { PlayInfo } from '@module/apis/client-side/live/type';
import { LoginStatusDiffQueryCacheInvalidator } from '@module/components/provider/LoginStatusDiffQueryCacheInvalidator';
import SettingConfig from '@module/config/setting';
import { useFetchLivePlayMutationStore } from '@module/stores/useFetchLivePlayMutationStore';
import { setContext, setTag, setUser } from '@sentry/nextjs';
import { useEffect } from 'react';
import useGetLoginInfoQuery from './auth/useGetLoginInfoQuery';
import UserInfoListeners from './UserInfoListeners';

/**
 * Sentry 세션 컨텍스트(유저 + 방)를 한곳에서 관리한다.
 *  - 유저: 로그인 정보가 바뀔 때마다 갱신. sessKey 등 민감정보가 새지 않도록 idx/nick만 전달하고,
 *    로그아웃 시 초기화한다(로그아웃은 loginInfo가 null이 아니라 userInfo.isLogin === false로 판별).
 *  - 방: /play 렌더 라이프사이클과 분리해 playInfo zustand 스토어를 직접 구독하고,
 *    media.code(방 식별자)가 바뀔 때만 갱신 — 방 전환/이탈이 그대로 태그에 반영된다.
 */
function useSentryUser() {
  const $ = _c(8);
  const {
    userInfo
  } = useGetLoginInfoQuery();
  let t0;
  if ($[0] !== userInfo) {
    t0 = () => {
      if (userInfo?.isLogin) {
        setUser({
          id: String(userInfo.idx),
          username: userInfo.nickJa || userInfo.nick
        });
      } else {
        setUser(null);
      }
    };
    $[0] = userInfo;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = userInfo?.isLogin;
  const t2 = userInfo?.idx;
  const t3 = userInfo?.nick;
  const t4 = userInfo?.nickJa;
  let t5;
  if ($[2] !== t1 || $[3] !== t2 || $[4] !== t3 || $[5] !== t4) {
    t5 = [t1, t2, t3, t4];
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  useEffect(t0, t5);
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  useEffect(_temp2, t6);
}

/**
 * 시청 중인 라이브 방 정보를 Sentry 태그/컨텍스트로 심는다. playInfo가 없으면(방 이탈) 초기화한다.
 * 에러 이벤트에서 "어느 방/어느 BJ에서 터졌는지" 바로 확인하기 위한 용도.
 */
function _temp2() {
  setSentryRoomContext(useFetchLivePlayMutationStore.getState().data);
  return useFetchLivePlayMutationStore.subscribe(_temp);
}
function _temp(state, prev) {
  if (state.data?.media.code !== prev.data?.media.code) {
    setSentryRoomContext(state.data);
  }
}
function setSentryRoomContext(playInfo: PlayInfo | undefined) {
  const media = playInfo?.media;
  if (!media) {
    setTag('bj_id', undefined);
    setTag('media_code', undefined);
    setTag('media_type', undefined);
    setTag('partner_code', undefined);
    setContext('room', null);
    return;
  }
  const partnerCode = SettingConfig.iframeASPConfig?.aspInfo?.code ?? SettingConfig.newASPPreset?.aspInfo?.code;
  setTag('bj_id', media.userId);
  setTag('media_code', media.code);
  setTag('media_type', media.onAirType ?? (media.isLive ? 'live' : 'rec'));
  setTag('partner_code', partnerCode);
  setContext('room', {
    bj_id: media.userId,
    bj_idx: media.userIdx,
    bj_nick: media.userNickJa || media.userNick,
    media_code: media.code,
    room_type: media.type,
    is_pw: media.isPw,
    latency_mode: media.latencyMode,
    channel: playInfo?.channel,
    partner_entry: !!partnerCode,
    partner_code: partnerCode
  });
}

/**
 * 전 앱이 공통으로 마운트하는 세션 리스너 집합. 앱별 정책은 프롭이 아니라 SettingConfig로 내부 분기한다
 * (레포 관례: 브랜드/플랫폼 분기는 ENVIRONMENT_STATUS에서 파생, ad-hoc 체크 금지).
 *  - Sentry 유저 컨텍스트: 전 앱 공통
 *  - Sentry 방 컨텍스트: 전 앱 공통 (playInfo 스토어 직접 구독, 아래 참고)
 *  - 로그아웃 응답 감지 후 쿼리 캐시 무효화: 전 앱 공통
 *  - 채널탭 리다이렉트 + GTM dataLayer: PC 소비자 사이트(pandatv-pc/fanza-pc)에서만
 *
 * 하위 UserInfoListeners가 usePathname을 쓰므로, 각 레이아웃에서 <Suspense> 아래에 마운트한다
 * (usePathname이 cacheComponents 캐시를 깨뜨리지 않도록).
 * 단, pandatv-pc의 (asp) 라우트 그룹은 라우트-컨텍스트라 SettingConfig로 표현 불가 → 거기선 마운트하지 않는다.
 */
export default function UserSessionListeners() {
  const $ = _c(1);
  useSentryUser();
  const isPcConsumerSite = !SettingConfig.isMoWeb && !SettingConfig.isSales;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <><LoginStatusDiffQueryCacheInvalidator />{isPcConsumerSite && <UserInfoListeners />}</>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}